/**
 * Known objects you can measure a photo against.
 *
 * INCHR, the tool Strandr gives away, offers exactly three: double garage door,
 * single garage door and standard door. These are those plus the ones that
 * actually appear on a South Florida property.
 *
 * ACCURACY NOTE, and it matters: a single reference length gives one scale
 * factor for the whole photo. That is only correct for things in the same
 * plane, at the same distance from the camera, as the reference. A roofline
 * running away from the camera gets under measured. The fixes, in order of
 * effort, are on the backlog:
 *   1. A second reference at a different depth, then interpolate.
 *   2. A ground plane homography from a known rectangle (a garage door is
 *      16 ft by 7 ft), which measures anything on that plane exactly.
 *   3. Trace the roof on aerial imagery, where scale is exact and there is no
 *      perspective at all. This is how the real measurement companies do it.
 */

export interface ReferenceObject {
  id: string
  label: string
  /** Real world length in feet. */
  feet: number
  /** How it is normally measured in a straight on photo. */
  axis: 'horizontal' | 'vertical'
  hint: string
}

export const REFERENCES: ReferenceObject[] = [
  {
    id: 'garage2',
    label: 'Double garage door',
    feet: 16,
    axis: 'horizontal',
    hint: 'The most reliable thing on a Florida house. 16 ft, edge to edge.',
  },
  {
    id: 'garage1',
    label: 'Single garage door',
    feet: 8,
    axis: 'horizontal',
    hint: '8 ft, edge to edge.',
  },
  {
    id: 'door',
    label: 'Front door',
    feet: 6.667,
    axis: 'vertical',
    hint: '6 ft 8 in. Measure the door slab, not the frame.',
  },
  {
    id: 'block',
    label: 'Concrete block course',
    feet: 1.333,
    axis: 'vertical',
    hint: '16 in per course. Count several courses for a better reading.',
  },
  {
    id: 'brick',
    label: 'Paver, standard',
    feet: 0.667,
    axis: 'horizontal',
    hint: '8 in. Best used across a run of pavers, not one.',
  },
  {
    id: 'window',
    label: 'Single hung window',
    feet: 3,
    axis: 'horizontal',
    hint: '36 in is the common width, but check it.',
  },
  {
    id: 'custom',
    label: 'Something else',
    feet: 0,
    axis: 'horizontal',
    hint: 'Anything you can measure with a tape. Type the real length.',
  },
]

export const referenceById = (id: string) =>
  REFERENCES.find((r) => r.id === id) ?? REFERENCES[0]
